import React, { useEffect, useMemo, useRef, useState } from "react";

import moment, { Moment } from "moment";
import { v4 as uuid } from "uuid";

import {
  formatNumber,
  getLocalStorageBulkEditWarningDismissed,
  getLocalStorageBulkReplaceConfirmationDismissed,
  isFieldEnrolled,
  setLocalStorageBulkEditWarningDismissed,
  setLocalStorageBulkReplaceConfirmationDismissed,
  useAddFieldPracticeParticipations,
  useDeleteFieldPracticeParticipations,
  useGetAllFieldPracticesByFarm,
  useGetFarms,
  useGetFieldsByFarmId,
  useGetPracticeCrops,
  usePracticeManagementCropParameter,
  useTranslation,
} from "utils";

import { getUnitDisplayLabel } from "consts";

import { CROP_TYPE_FCPO, CropFCPO, FieldListField, PRACTICE_SLUG, PracticeBundle, PracticeCrop, PracticeFCPO, PracticeSlug, WithInjectedName } from "models";

import {
  AlertBanner,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Form,
  type FormInstance,
  Icon,
  Link,
  Modal,
  StickyFooterButtons,
  TagNormal,
  Tooltip,
  Typography,
  Message as message,
} from "components";

import { UndersowingOverlapWarningModal } from "pages/app/all-field-view/components/UndersowingOverlapWarningModal.tsx";
import { CropYieldSection } from "pages/app/all-field-view/components/forms/crop/CropYieldSection.tsx";
import { SecondaryDataCropForm } from "pages/app/all-field-view/components/forms/crop/SecondaryDataCropForm.tsx";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers.tsx";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm.tsx";
import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getSharedSecondaryDataAnswer, isSecondaryDataQuestionShown } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { BulkAddEditCropFormProps, getDefaultBulkDataEntryDateValues, getResidueInfo } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getUndersowingWarningsForChanges } from "pages/app/all-field-view/utils/CropValidationUtils";
import { type MissingDataForm, YIELD_PSEUDO_PRACTICE_TYPE } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { BulkCropNameSection } from "pages/app/historical-practice-data-table-view/components/BulkCropNameSection.tsx";
import { BulkDataPointSection, type BulkVerifiedField } from "pages/app/historical-practice-data-table-view/components/BulkDataPointSection.tsx";
import { BulkDataPointSelector } from "pages/app/historical-practice-data-table-view/components/BulkDataPointSelector.tsx";
import { BulkFieldBreakdown } from "pages/app/historical-practice-data-table-view/components/BulkFieldBreakdown.tsx";
import { BulkResidueSection } from "pages/app/historical-practice-data-table-view/components/BulkResidueSection.tsx";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import { MissingCropGrowingPeriod } from "pages/app/historical-practice-data-table-view/components/MissingCropGrowingPeriod";
import { MissingSeedingForm } from "pages/app/historical-practice-data-table-view/components/MissingSeedingForm.tsx";
import { type BulkCropDataPointEdit, useBulkCropSave } from "pages/app/historical-practice-data-table-view/hooks/useBulkCropSave";
import { useBulkPracticeSave } from "pages/app/historical-practice-data-table-view/hooks/useBulkPracticeSave";
import {
  BULK_CROP_DATA_POINT,
  BULK_DATA_DRAWER_MODE,
  type BulkDataDrawerMode,
  type BulkDataPointType,
  type BulkEntryAppliedTo,
  IS_ITEM_EDITABLE,
  getApplicableDataPointTypes,
  getBulkDataPointStates,
  getBulkFieldBreakdown,
  getBulkPracticeConfig,
  getEntryAppliedTo,
  getEntryPendingApplication,
  getFirstEditableDataPoint,
  getOverwrittenFieldIds,
  isBulkCropDataPoint,
  isBulkDataPointSelectable,
  isItemWritable,
  isMultiValueDataPoint,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

export type BulkDataDrawerProps = {
  showDrawer: boolean;
  onClose: () => void;
  missingDataFormItems: MissingDataForm[];
  selectedYear: number;
  // "add" fills only the crops missing a value, "edit" replaces the value on every crop still open for editing
  mode: BulkDataDrawerMode;
  // which data points the calling view offers — e.g. a view without a mineral fertilizer column leaves that card out
  availablePracticeTypes: BulkDataPointType[];
  // opens on a specific card, e.g. when the flow started from one cell; otherwise the first editable one wins
  initialPracticeType?: BulkDataPointType;
  onCropsSkipped?: (skippedCropIds: string[]) => void;
  onSubmitSuccess?: () => void;
};

type BulkDataPointFormProps = {
  practiceType: BulkDataPointType;
  isShown: boolean;
  formsByPracticeType: React.MutableRefObject<Partial<Record<BulkDataPointType, FormInstance>>>;
  onEdited: (practiceType: BulkDataPointType) => void;
  children: React.ReactNode;
};

type BulkEntryAppliedTagProps = {
  appliedTo: BulkEntryAppliedTo;
  fields: FieldListField[];
};

type BulkReplaceConfirmationModalProps = {
  isVisible: boolean;
  // fields whose existing value the save is about to replace
  overwrittenFieldCount: number;
  onConfirm: () => void;
  onCancel: () => void;
};

type BulkEditWarningBannerProps = {
  mode: BulkDataDrawerMode;
};

// how long the success tick stays up before the drawer closes itself
const SUCCESS_OVERLAY_MS = 1500;

const FakeOrganicFertilizerPracticeBundle: PracticeBundle = {
  active: true,
  hasConfirmedData: false,
  slug: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
  startYear: null,
  discontinuedYear: null,
  reactivatedYear: null,
  enrollable: true,
};

// Bulk data drawer (PE-972): the farmer picks one data point for the whole selection, fills it once, and the save
// writes it to every selected crop the mode allows. The single-cell flow stays on AddEditMissingDataDrawer.
export const BulkDataDrawer = ({
  showDrawer,
  onClose,
  missingDataFormItems,
  selectedYear,
  mode,
  availablePracticeTypes,
  initialPracticeType,
  onCropsSkipped,
  onSubmitSuccess,
}: BulkDataDrawerProps) => {
  const { t } = useTranslation(["historicalPracticeDataTableView", "allFieldView"]);
  const { submissionStatusValue } = useSubmissionStatus();
  const [selectedPracticeType, setSelectedPracticeType] = useState<BulkDataPointType | undefined>(initialPracticeType);

  const [openedPracticeTypes, setOpenedPracticeTypes] = useState<BulkDataPointType[]>([]);
  const [editedPracticeTypes, setEditedPracticeTypes] = useState<BulkDataPointType[]>([]);
  const formsByPracticeType = useRef<Partial<Record<BulkDataPointType, FormInstance>>>({});
  const [areDetailsShown, setAreDetailsShown] = useState<boolean>(false);
  const [isReplaceConfirmationVisible, setIsReplaceConfirmationVisible] = useState<boolean>(false);
  const [isUndersowingWarningVisible, setIsUndersowingWarningVisible] = useState<boolean>(false);
  const [undersowingCropsBrokenByChange, setUndersowingCropsBrokenByChange] = useState<string[]>([]);
  const [hasSavedSuccessfully, setHasSavedSuccessfully] = useState<boolean>(false);

  const [wasWarningDismissedBefore] = useState<boolean>(() => getLocalStorageBulkEditWarningDismissed());
  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<Moment>(moment());
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<Moment>(moment());

  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: farmFields } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const { data: practiceCrops } = useGetPracticeCrops({});

  const selectedCrops = useMemo(
    () => missingDataFormItems.map((item) => item.crop).filter((crop): crop is CropFCPO => crop !== undefined),
    [missingDataFormItems]
  );
  // the growing period form seeds itself from this, so it may not be rebuilt on every render
  const selectedCropsWithFieldIds = useMemo(
    () =>
      missingDataFormItems
        .filter((item): item is MissingDataForm & { crop: CropFCPO } => item.crop != null)
        .map((item) => ({ crop: item.crop, fieldId: item.fieldId })),
    [missingDataFormItems]
  );
  const fieldIdsWithSelectedCrops = useMemo(() => Array.from(new Set(missingDataFormItems.map((item) => item.fieldId))), [missingDataFormItems]);
  const selectedFields = useMemo(
    () => (farmFields ?? []).filter((field) => fieldIdsWithSelectedCrops.includes(field.id)),
    [farmFields, fieldIdsWithSelectedCrops]
  );
  const totalHaOfSelectedFields = useMemo(() => selectedFields.reduce((total, field) => total + (field.size ?? 0), 0), [selectedFields]);

  // crop name and residue both need the crop catalogue: residue is only offered for crops that produce any, and the
  // request builder nulls hasResidue out for the ones that don't
  const selectionCropType = selectedCrops[0]?.cropType ?? CROP_TYPE_FCPO.MAIN;
  const sharedCropId = selectedCrops.every((crop) => crop.cropId === selectedCrops[0]?.cropId) ? selectedCrops[0]?.cropId ?? null : null;
  const residueInfo = useMemo(
    () =>
      getResidueInfo({
        selectedItems: missingDataFormItems
          .filter((item) => item.crop !== undefined)
          .map((item) => ({
            ...item.crop!,
            fieldId: item.fieldId,
            isPlaceholder: false,
            isMandatory: false,
            isBaseline: false,
            availablePracticeBundles: [],
            fertilizerManagement: undefined,
          })),
        cropsToDisplay: (practiceCrops ?? []) as WithInjectedName<PracticeCrop>[],
        selectedCropId: sharedCropId,
        onlyPlaceholderCrops: false,
      }),
    [missingDataFormItems, practiceCrops, sharedCropId]
  );

  const [pickedCropId, setPickedCropId] = useState<string | undefined>(undefined);
  const pickedCrop = practiceCrops?.find((practiceCrop) => practiceCrop.id === pickedCropId);
  const showResidue = pickedCrop ? !!pickedCrop.residue && !pickedCrop.residueHidden : residueInfo.showResidue;
  const residueType = pickedCrop ? pickedCrop.residueType : residueInfo.residueType;
  const residueCrops = useMemo(
    () => (pickedCrop ? [pickedCrop] : (practiceCrops ?? []).filter((practiceCrop) => selectedCrops.some((crop) => crop.cropId === practiceCrop.id))),
    [pickedCrop, practiceCrops, selectedCrops]
  );
  const areNoSeedingCropsSelected = (
    pickedCrop ? [pickedCrop] : selectedCrops.map((crop) => practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.cropId))
  ).every((practiceCrop) => practiceCrop?.isPerennial);
  const applicableDataPointTypes = useMemo(
    () =>
      getApplicableDataPointTypes(availablePracticeTypes, {
        cropTypes: selectedCrops.map((crop) => crop.cropType),
        showResidue,
      }),
    [availablePracticeTypes, selectedCrops, showResidue]
  );
  // an edit on a card that is no longer offered (e.g. residue after picking a crop without any) is not saved
  const activeEditedPracticeTypes = editedPracticeTypes.filter((practiceType) => applicableDataPointTypes.includes(practiceType));

  const dataPointStates = useMemo(
    () => getBulkDataPointStates(missingDataFormItems, applicableDataPointTypes, mode),
    [missingDataFormItems, applicableDataPointTypes, mode]
  );
  const breakdownRows = useMemo(() => getBulkFieldBreakdown(missingDataFormItems, applicableDataPointTypes), [missingDataFormItems, applicableDataPointTypes]);

  useEffect(() => {
    if (!showDrawer) return;
    const isSelectable = (practiceType: BulkDataPointType | undefined) =>
      dataPointStates.some((dataPointState) => dataPointState.practiceType === practiceType && isBulkDataPointSelectable(dataPointState));
    if (isSelectable(selectedPracticeType)) return;

    setSelectedPracticeType(isSelectable(initialPracticeType) ? initialPracticeType : getFirstEditableDataPoint(dataPointStates));
  }, [showDrawer, selectedPracticeType, initialPracticeType, dataPointStates]);

  useEffect(() => {
    if (!selectedPracticeType) return;
    setOpenedPracticeTypes((previousTypes) => (previousTypes.includes(selectedPracticeType) ? previousTypes : [...previousTypes, selectedPracticeType]));
  }, [selectedPracticeType]);

  const mountedPracticeTypes =
    selectedPracticeType && !openedPracticeTypes.includes(selectedPracticeType) ? [...openedPracticeTypes, selectedPracticeType] : openedPracticeTypes;

  useEffect(() => {
    const { seedingDate, harvestDate } = getDefaultBulkDataEntryDateValues(selectedCrops);
    const yearFallback = moment().year(selectedYear);
    setRecommendedSeedingDate(seedingDate.isValid() ? seedingDate : yearFallback);
    setRecommendedHarvestDate(harvestDate.isValid() ? harvestDate : yearFallback);
  }, [selectedCrops, selectedYear]);

  // growing-period validation needs the other crops already on each selected field, minus the ones being edited
  const existingCropsForValidationByField = useMemo(() => {
    if (!allFieldsPractices) return {};
    const editedCropIds = new Set(missingDataFormItems.map((item) => item.key));

    return fieldIdsWithSelectedCrops.reduce((cropsByField: Record<string, CropFCPO[]>, fieldId) => {
      const fieldCrops = allFieldsPractices[fieldId]?.crops ?? [];
      return { ...cropsByField, [fieldId]: fieldCrops.filter((crop) => crop.year === selectedYear && !editedCropIds.has(crop.id)) };
    }, {});
  }, [allFieldsPractices, missingDataFormItems, selectedYear, fieldIdsWithSelectedCrops]);

  const selectedTillages = missingDataFormItems.flatMap((item) => item.tillages ?? []);
  const selectedOrganicFertilizers = missingDataFormItems.flatMap((item) => item.organicFertilizers ?? []);
  const selectedMineralFertilizers = missingDataFormItems.flatMap((item) => item.mineralFertilizers ?? []);

  const { save: saveCropData, isSaving: isSavingCropData } = useBulkCropSave({ missingDataFormItems, fields: selectedFields, mode });
  const { save, isSaving } = useBulkPracticeSave({
    missingDataFormItems,
    existingPracticesInForm: [...selectedOrganicFertilizers, ...selectedMineralFertilizers, ...selectedTillages],
    mode,
  });

  // bundle and enrolment are read off the first field; activation writes to all selected fields
  const mineralFertilizerFieldId = missingDataFormItems[0]?.fieldId;
  const isMineralFertilizerFieldEnrolled = isFieldEnrolled(
    allFieldsPractices?.[mineralFertilizerFieldId]?.availablePracticeBundles ?? [],
    currentFarm?.carbonProgram ?? null
  );
  const [mineralPracticeBundles, setMineralPracticeBundles] = useState<PracticeBundle[]>([]);
  const mineralPracticeBundle = useMemo(
    () =>
      mineralPracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS) ??
      (allFieldsPractices?.[mineralFertilizerFieldId]?.availablePracticeBundles ?? []).find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS),
    [mineralPracticeBundles, allFieldsPractices, mineralFertilizerFieldId]
  );
  const { mutate: addFieldPracticeParticipations } = useAddFieldPracticeParticipations(fieldIdsWithSelectedCrops, currentFarm?.id);
  const { mutate: deleteFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(fieldIdsWithSelectedCrops, currentFarm?.id);

  const handleMineralBundleActivation = async (bundleSlug: PracticeSlug, active: boolean) => {
    if (active) {
      addFieldPracticeParticipations(fieldIdsWithSelectedCrops.map((fieldId) => ({ fieldId, practiceSlugs: [bundleSlug] })));
    } else {
      fieldIdsWithSelectedCrops.forEach((fieldId) => deleteFieldPracticeParticipations({ fieldId, practiceSlugs: [bundleSlug] }));
    }

    setMineralPracticeBundles((previousBundles) => {
      const existingBundle = previousBundles.find((bundle) => bundle.slug === bundleSlug);
      const updatedBundle: PracticeBundle = {
        hasConfirmedData: false,
        startYear: null,
        discontinuedYear: null,
        reactivatedYear: null,
        enrollable: true,
        ...existingBundle,
        active,
        slug: bundleSlug,
      };

      return [...previousBundles.filter((bundle) => bundle.slug !== bundleSlug), updatedBundle];
    });
  };

  // Yield
  const cropForYield = missingDataFormItems[0]?.crop;
  const cropDetailsForYield = practiceCrops?.find((practiceCrop) => practiceCrop.id === cropForYield?.cropId);
  const cropPMSQuery = usePracticeManagementCropParameter(
    selectedCrops[0]?.cropId,
    currentFarm?.carbonProgram?.country.toLowerCase() as PracticeManagement.CountryCode
  );
  const yieldValues = missingDataFormItems.map((item) => item.yieldPerHa ?? null);
  const initialYieldValue = yieldValues.every((value) => value === yieldValues[0]) ? yieldValues[0] : null;
  const yieldUnitValues = missingDataFormItems.map((item) => item.yieldPerHaUnit ?? null);
  const initialYieldUnitValue = yieldUnitValues.every((value) => value === yieldUnitValues[0]) ? yieldUnitValues[0] : null;

  const hasMixedYield = !yieldValues.every((value) => value === yieldValues[0]) || !yieldUnitValues.every((value) => value === yieldUnitValues[0]);

  const [committedYield, setCommittedYield] = useState<number | null>(initialYieldValue);
  const [committedYieldUnit, setCommittedYieldUnit] = useState<typeof initialYieldUnitValue>(initialYieldUnitValue);

  const handleDataPointSelect = (nextPracticeType: BulkDataPointType) => {
    setSelectedPracticeType(nextPracticeType);
    requestAnimationFrame(
      () =>
        document
          .querySelector('[data-testid="bulk-data-point-form"]:not([hidden]) .bulk-data-point-section')
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const markAsEdited = (practiceType: BulkDataPointType) => {
    if (isDataPointReadOnly(practiceType)) return;
    setEditedPracticeTypes((previousTypes) => (previousTypes.includes(practiceType) ? previousTypes : [...previousTypes, practiceType]));
  };

  // the overwrite warning only applies to edit mode; add mode never replaces a value, and its banner says so
  const overwrittenFieldIds =
    mode === BULK_DATA_DRAWER_MODE.edit
      ? Array.from(new Set(activeEditedPracticeTypes.flatMap((practiceType) => getOverwrittenFieldIds(missingDataFormItems, practiceType))))
      : [];

  const isCropLevelDataPoint = (practiceType: BulkDataPointType) => isBulkCropDataPoint(practiceType) || practiceType === PRACTICE_SLUG.CROPS;

  const toCropFormValues = (practiceType: BulkDataPointType, formValues: Record<string, unknown>) => {
    if (practiceType !== PRACTICE_SLUG.CROPS) return formValues as BulkAddEditCropFormProps;

    const growingPeriod = {
      ...("seedingDateRange" in formValues ? { seedingDateRange: formValues.seedingDateRange } : {}),
      ...("harvestDateRange" in formValues ? { harvestDateRange: formValues.harvestDateRange } : {}),
    };

    return { ...formValues, crop: { ...((formValues.crop as Record<string, unknown>) ?? {}), ...growingPeriod } } as BulkAddEditCropFormProps;
  };

  const handleSubmit = async () => {
    const practiceTypesToSave = activeEditedPracticeTypes.filter((practiceType) => formsByPracticeType.current[practiceType]);
    if (practiceTypesToSave.length === 0) return;

    const validations = await Promise.all(
      practiceTypesToSave.map(
        (practiceType) =>
          formsByPracticeType.current[practiceType]?.validateFields().then(
            () => true,
            () => false
          )
      )
    );
    const firstInvalidPracticeType = practiceTypesToSave.find((_, index) => !validations[index]);
    if (firstInvalidPracticeType) {
      setSelectedPracticeType(firstInvalidPracticeType);
      message({ content: t("allFieldView:errors.required"), icon: "error" });
      return;
    }

    if (overwrittenFieldIds.length > 0 && !getLocalStorageBulkReplaceConfirmationDismissed() && !isReplaceConfirmationVisible) {
      setIsReplaceConfirmationVisible(true);
      return;
    }

    const getFormValues = (practiceType: BulkDataPointType, onlyTouched: boolean): Record<string, unknown> =>
      onlyTouched
        ? formsByPracticeType.current[practiceType]?.getFieldsValue({ filter: (meta) => !!meta.touched }) ?? {}
        : formsByPracticeType.current[practiceType]?.getFieldsValue(true) ?? {};

    // crop-level data points share one request, since each one rewrites the whole crop
    const cropLevelPracticeTypes = practiceTypesToSave.filter(isCropLevelDataPoint);
    const cropLevelEdits: BulkCropDataPointEdit[] = cropLevelPracticeTypes.map((practiceType) => ({
      dataPointType: practiceType,
      formValues: toCropFormValues(practiceType, getFormValues(practiceType, false)),
      touchedFormFields: toCropFormValues(practiceType, getFormValues(practiceType, true)),
    }));
    const practiceLevelPracticeTypes = practiceTypesToSave.filter((practiceType) => !isCropLevelDataPoint(practiceType));

    // mirrors the single-crop/bulk-edit drawers' own check (CropValidationUtils.ts) — a growing-period edit here
    // can just as easily orphan an existing Undersowing crop's overlap, across every field in the selection
    if (!isUndersowingWarningVisible) {
      const cropsEdit = cropLevelEdits.find((edit) => edit.dataPointType === PRACTICE_SLUG.CROPS);
      const seedingDateRange = cropsEdit?.formValues.crop?.seedingDateRange as [Moment | null, Moment | null] | undefined;
      const harvestDateRange = cropsEdit?.formValues.crop?.harvestDateRange as [Moment | null, Moment | null] | undefined;

      if (seedingDateRange?.[0] && harvestDateRange?.[1]) {
        const changes = missingDataFormItems
          .filter((item): item is MissingDataForm & { crop: CropFCPO } => !!item.crop && isItemWritable(item, PRACTICE_SLUG.CROPS, mode))
          .map((item) => ({
            fieldId: item.fieldId,
            crop: { ...item.crop, seedingStartDate: seedingDateRange[0]!.toISOString(), harvestEndDate: harvestDateRange[1]!.toISOString() },
          }));
        const undersowingWarnings = getUndersowingWarningsForChanges(
          changes,
          (fieldId) => allFieldsPractices?.[fieldId]?.crops ?? [],
          currentFarm?.carbonProgram
        );

        if (undersowingWarnings.length > 0) {
          setUndersowingCropsBrokenByChange(undersowingWarnings);
          setIsUndersowingWarningVisible(true);
          return;
        }
      }
    }
    // both gates are now satisfied for good — reset them together so neither re-triggers if the other's own
    // confirm click causes handleSubmit to run again (each gate's own reset only holds for one re-entrant call)
    setIsReplaceConfirmationVisible(false);
    setIsUndersowingWarningVisible(false);
    setUndersowingCropsBrokenByChange([]);

    // the crop-level request carries the crop's seeding and practices as they were when the drawer opened, so it goes
    // first and the practice saves land on top of it
    const didSaveCropData = cropLevelEdits.length > 0 ? await saveCropData(cropLevelEdits) : true;
    const practiceResults = await Promise.all(practiceLevelPracticeTypes.map((practiceType) => save(practiceType, getFormValues(practiceType, false))));
    const savedPracticeTypes = [
      ...(didSaveCropData ? cropLevelPracticeTypes : []),
      ...practiceLevelPracticeTypes.filter((_, index) => practiceResults[index].didSave),
    ];
    // what went through is no longer pending, so saving again after a failure only retries the rest
    setEditedPracticeTypes((previousTypes) => previousTypes.filter((practiceType) => !savedPracticeTypes.includes(practiceType)));
    if (savedPracticeTypes.length < practiceTypesToSave.length) return;

    const skippedCropIds = Array.from(new Set(practiceResults.flatMap((result) => result.skippedCropIds)));
    onSubmitSuccess?.();
    if (skippedCropIds.length > 0) onCropsSkipped?.(skippedCropIds);

    setHasSavedSuccessfully(true);
    setTimeout(onClose, SUCCESS_OVERLAY_MS);
  };

  const isAddMode = mode === BULK_DATA_DRAWER_MODE.add;

  // add mode only ever creates entries, so each one promises where it is about to land; edit mode names the crops the
  // existing entry already sits on
  const isNewEntry = (practiceType: BulkDataPointType, entry: PracticeFCPO) => isAddMode || !getEntryAppliedTo(missingDataFormItems, practiceType, entry);
  const renderEntryTag = (practiceType: BulkDataPointType) => (entry: PracticeFCPO) => {
    const appliedTo = isNewEntry(practiceType, entry)
      ? getEntryPendingApplication(missingDataFormItems, practiceType, mode)
      : getEntryAppliedTo(missingDataFormItems, practiceType, entry);

    return appliedTo ? <BulkEntryAppliedTag appliedTo={appliedTo} fields={selectedFields} /> : null;
  };
  const renderEntryTitle = (practiceType: BulkDataPointType) => (entry: PracticeFCPO) =>
    isNewEntry(practiceType, entry) ? t("historicalPracticeDataTableView:bulkDataDrawer.newApplication") : undefined;

  const getDataPointState = (practiceType: BulkDataPointType) => dataPointStates.find((dataPointState) => dataPointState.practiceType === practiceType);
  // residue is named after what the crop leaves behind, as in the single-crop form — "Biomass management" for an undersowing
  const labelOverrides: Partial<Record<BulkDataPointType, string>> = residueType
    ? { [BULK_CROP_DATA_POINT.residue]: t(`allFieldView:cropForm.residue.${residueType}.title`) }
    : {};
  const getDataPointLabel = (practiceType: BulkDataPointType) => {
    const dataPointState = getDataPointState(practiceType);
    if (!dataPointState) return "";
    return labelOverrides[practiceType] ?? t(`historicalPracticeDataTableView:bulkDataDrawer.dataPoints.${dataPointState.labelKey}`);
  };
  // nothing in the selection may be written: the data point opens so its values can be read, and the save is off
  const isDataPointReadOnly = (practiceType: BulkDataPointType) => getDataPointState(practiceType)?.writableCount === 0;

  // the crops the save writes to: those of every edited data point, or of the open one until something is edited
  const countedPracticeTypes = activeEditedPracticeTypes.length > 0 ? activeEditedPracticeTypes : selectedPracticeType ? [selectedPracticeType] : [];
  const writableItems = missingDataFormItems.filter((item) => countedPracticeTypes.some((practiceType) => isItemWritable(item, practiceType, mode)));
  const writableFieldCount = new Set(writableItems.map((item) => item.fieldId)).size;

  const secondaryDataCrops = missingDataFormItems
    .filter((item) => isItemWritable(item, BULK_CROP_DATA_POINT.cropName, mode))
    .map((item) => item.crop)
    .filter((crop): crop is CropFCPO => crop !== undefined);

  const isSecondaryDataShown = isSecondaryDataQuestionShown(secondaryDataCrops, currentFarm?.carbonProgram?.accountingMethodology);

  const getExplainer = (practiceType: BulkDataPointType) => {
    const dataPoint = getDataPointLabel(practiceType).toLowerCase();
    if (isDataPointReadOnly(practiceType)) return t("historicalPracticeDataTableView:bulkDataDrawer.verifiedTooltip", { dataPoint });
    if (!isAddMode) {
      return isMultiValueDataPoint(practiceType)
        ? t("historicalPracticeDataTableView:bulkDataDrawer.editEntryExplainer", { dataPoint })
        : t("historicalPracticeDataTableView:bulkDataDrawer.explainer", { dataPoint });
    }

    const count = getDataPointState(practiceType)?.writableCount ?? 0;
    return isMultiValueDataPoint(practiceType)
      ? t("historicalPracticeDataTableView:bulkDataDrawer.addEntryExplainer", { count, dataPoint })
      : t("historicalPracticeDataTableView:bulkDataDrawer.addExplainer", { count, dataPoint });
  };

  // the crops this save will skip, so the section can list them instead of silently leaving them out
  const getVerifiedFields = (practiceType: BulkDataPointType): BulkVerifiedField[] => {
    const isItemEditable = IS_ITEM_EDITABLE[practiceType];

    return missingDataFormItems
      .filter((item) => !isItemEditable?.(item))
      .map((item) => ({
        cropId: item.key,
        fieldId: item.fieldId,
        valueLabel:
          practiceType === YIELD_PSEUDO_PRACTICE_TYPE && item.yieldPerHa != null
            ? `${formatNumber({ number: item.yieldPerHa })} ${getUnitDisplayLabel(item.yieldPerHaUnit)}`
            : undefined,
      }));
  };

  // the practice and yield forms head their own grey panel; the crop-level data points need the section to draw one
  const doesFormProvideOwnPanel = (practiceType: BulkDataPointType) => isMultiValueDataPoint(practiceType) || practiceType === YIELD_PSEUDO_PRACTICE_TYPE;

  const renderDataPointInputs = (practiceType: BulkDataPointType) => {
    const isReadOnly = isDataPointReadOnly(practiceType);
    const practiceConfigForBulk = isMultiValueDataPoint(practiceType)
      ? getBulkPracticeConfig(missingDataFormItems, practiceType, mode, isReadOnly, submissionStatusValue?.lastSubmissionYear)
      : undefined;

    if (practiceType === BULK_CROP_DATA_POINT.cropName) {
      return (
        <BulkCropNameSection
          cropType={selectionCropType}
          practiceCrops={practiceCrops}
          sharedCropId={sharedCropId}
          showResidue={residueInfo.showResidue}
          isLoading={!practiceCrops}
          disabled={isReadOnly}
          onCropPicked={setPickedCropId}
        />
      );
    }
    if (practiceType === BULK_CROP_DATA_POINT.residue) {
      return (
        <>
          <Form.Item name={[CROP, "showResidue"]} hidden={true} initialValue={true}>
            {/* antd requires a valid child; the value is only carried for the request builder */}
            <div />
          </Form.Item>
          <BulkResidueSection
            cropType={selectionCropType}
            residueType={residueType}
            residueCrops={residueCrops}
            isResidueProduced={residueInfo.isResidueProduced}
            hasMixedResidue={residueInfo.hasMixedResidue}
            residueNote={residueInfo.residueNote}
            hasMixedResidueNotes={residueInfo.hasMixedResidueNotes}
            disabled={isReadOnly}
          />
        </>
      );
    }
    if (practiceType === PRACTICE_SLUG.CROPS) {
      return (
        <MissingCropGrowingPeriod
          recommendedSeedingDate={recommendedSeedingDate}
          recommendedHarvestDate={recommendedHarvestDate}
          existingCropsForValidationByField={existingCropsForValidationByField}
          selectedCropsWithFieldIds={selectedCropsWithFieldIds}
          disabled={isReadOnly}
        />
      );
    }
    if (practiceType === YIELD_PSEUDO_PRACTICE_TYPE) {
      return (
        <div className="add-edit-crop-form-container">
          <CropYieldSection
            label={t("allFieldView:cropForm.yield")}
            initialValue={initialYieldValue}
            initialValueUnit={initialYieldUnitValue}
            help={hasMixedYield ? t("allFieldView:cropForm.mixedValues") : undefined}
            initialIsHarvested={cropForYield?.wasHarvested ?? cropDetailsForYield?.harvestIsMandatory !== false}
            disabled={isReadOnly}
            isRequired={true}
            isZeroYieldsAllowed={cropDetailsForYield?.harvestIsMandatory === false}
            committedYield={committedYield}
            committedYieldUnit={committedYieldUnit}
            cropName={cropDetailsForYield?.name}
            yieldBenchmarkBoundsTperHa={cropPMSQuery.data?.harvestTonsPerHectare}
            onChange={(value, unit) => {
              setCommittedYield(value);
              setCommittedYieldUnit((unit as typeof initialYieldUnitValue) ?? null);
            }}
          />
        </div>
      );
    }
    if (practiceType === PRACTICE_SLUG.SEEDINGS) {
      return (
        <MissingSeedingForm
          seedingTypes={missingDataFormItems.flatMap((item) => item.seedings ?? [])}
          disabled={isReadOnly}
          allowNone={areNoSeedingCropsSelected}
        />
      );
    }
    if (practiceType === PRACTICE_SLUG.TILLAGES) {
      return (
        <AddEditTillageForm
          cropId={uuid()} // only needed to build a TILLAGE_BLANK_PRACTICE, never read back
          existingTillages={practiceConfigForBulk?.practiceToShow ?? []}
          // adding or removing entries stays locked with the yes/no question when a crop only has its dates or depth open
          disabled={isReadOnly || !!practiceConfigForBulk?.toggleDisabled}
          isReadOnly={isReadOnly}
          baselineYears={undefined}
          year={selectedYear}
          requirementConfig={{ tillageDepth: "requiredForSaving", dates: "requiredForSaving" }}
          optionalInputsAreShown={false}
          practiceConfigForBulk={practiceConfigForBulk}
          recommendedSeedingDate={recommendedSeedingDate.toISOString()}
          bulkEditMode={missingDataFormItems.length > 1}
          requirePreciseTillageDepth={!!currentFarm?.carbonProgram?.fieldsManagement.requirePreciseTillageDepth}
          renderEntryTag={renderEntryTag(PRACTICE_SLUG.TILLAGES)}
          renderEntryTitle={renderEntryTitle(PRACTICE_SLUG.TILLAGES)}
        />
      );
    }
    if (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
      return (
        <AddEditOrganicAndMineralFertilizers
          chosenFertilizerType="organic"
          // adding or removing entries stays locked with the yes/no question when a crop only has its dates or depth open
          disabled={isReadOnly || !!practiceConfigForBulk?.toggleDisabled}
          isReadOnly={isReadOnly}
          existingFertilizers={practiceConfigForBulk?.practiceToShow ?? []}
          baselineYears={undefined}
          year={selectedYear}
          practiceBundle={FakeOrganicFertilizerPracticeBundle}
          practiceConfigForBulk={practiceConfigForBulk}
          recommendedSeedingDate={recommendedSeedingDate.toISOString()}
          requirementConfig={{ dates: "requiredForSaving" }}
          bulkEditMode={missingDataFormItems.length > 1}
          renderEntryTag={renderEntryTag(PRACTICE_SLUG.ORGANIC_FERTILIZERS)}
          renderEntryTitle={renderEntryTitle(PRACTICE_SLUG.ORGANIC_FERTILIZERS)}
        />
      );
    }
    if (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
      return (
        <AddEditOrganicAndMineralFertilizers
          chosenFertilizerType="mineral"
          // adding or removing entries stays locked with the yes/no question when a crop only has its dates or depth open
          disabled={isReadOnly || !!practiceConfigForBulk?.toggleDisabled}
          isReadOnly={isReadOnly}
          existingFertilizers={practiceConfigForBulk?.practiceToShow ?? []}
          baselineYears={undefined}
          year={selectedYear}
          practiceBundle={mineralPracticeBundle}
          onBundleActivation={handleMineralBundleActivation}
          fieldIsEnrolled={isMineralFertilizerFieldEnrolled}
          practiceConfigForBulk={practiceConfigForBulk}
          recommendedSeedingDate={recommendedSeedingDate.toISOString()}
          requirementConfig={{ dates: "requiredForSaving" }}
          bulkEditMode={missingDataFormItems.length > 1}
          renderEntryTag={renderEntryTag(PRACTICE_SLUG.MINERAL_FERTILIZERS)}
          renderEntryTitle={renderEntryTitle(PRACTICE_SLUG.MINERAL_FERTILIZERS)}
        />
      );
    }
    return null;
  };

  const saveButtonLabel = isAddMode
    ? t(
        activeEditedPracticeTypes.length > 1
          ? "historicalPracticeDataTableView:bulkDataDrawer.applyDataPoints"
          : "historicalPracticeDataTableView:bulkDataDrawer.apply",
        { count: writableItems.length, dataPointCount: activeEditedPracticeTypes.length }
      )
    : t(
        activeEditedPracticeTypes.length > 1
          ? "historicalPracticeDataTableView:bulkDataDrawer.updateDataPoints"
          : "historicalPracticeDataTableView:bulkDataDrawer.update",
        { count: writableFieldCount, dataPointCount: activeEditedPracticeTypes.length }
      );

  return (
    <Drawer
      className="bulk-data-drawer"
      dataTestId="bulk-data-drawer"
      title={t(isAddMode ? "historicalPracticeDataTableView:bulkDataDrawer.addTitle" : "historicalPracticeDataTableView:bulkDataDrawer.editTitle")}
      visible={showDrawer}
      onClose={onClose}
      destroyOnHidden={true}
    >
      {hasSavedSuccessfully && <BulkSaveSuccessOverlay />}
      <Flex vertical gap="1rem">
        <Flex align="center" gap="1rem">
          <Flex align="center" justify="center" className="bulk-data-drawer-icon">
            <Icon iconName="tabler/copy" size="l" />
          </Flex>
          <Flex vertical flex="1">
            <Typography variant="h5" className="margin-0">
              {isAddMode
                ? t("historicalPracticeDataTableView:bulkDataDrawer.cropsSelected", { count: missingDataFormItems.length })
                : t("historicalPracticeDataTableView:drawer.header.fieldsSelected", { count: writableFieldCount })}
            </Typography>
            <Typography variant="paragraph-s" color="grey-600" className="margin-0">
              {formatNumber({ number: totalHaOfSelectedFields })} ha
            </Typography>
          </Flex>
          <Link dataTestId="bulk-data-drawer-details-toggle" type="primary-link" onClick={() => setAreDetailsShown((wereShown) => !wereShown)}>
            {t(areDetailsShown ? "historicalPracticeDataTableView:bulkDataDrawer.hideDetails" : "historicalPracticeDataTableView:bulkDataDrawer.showDetails")}
          </Link>
        </Flex>

        {areDetailsShown && <BulkFieldBreakdown rows={breakdownRows} fields={selectedFields} />}

        {!wasWarningDismissedBefore && <BulkEditWarningBanner mode={mode} />}

        <BulkDataPointSelector
          mode={mode}
          dataPointStates={dataPointStates}
          selectedPracticeType={selectedPracticeType}
          editedPracticeTypes={activeEditedPracticeTypes}
          labelOverrides={labelOverrides}
          onSelect={handleDataPointSelect}
        />

        {mountedPracticeTypes.map((practiceType) => (
          <BulkDataPointForm
            key={practiceType}
            practiceType={practiceType}
            isShown={practiceType === selectedPracticeType}
            formsByPracticeType={formsByPracticeType}
            onEdited={markAsEdited}
          >
            {practiceType === BULK_CROP_DATA_POINT.cropName && isSecondaryDataShown && (
              <div className="bulk-data-point-section margin-bottom-100">
                <div className="bulk-data-point-panel">
                  <SecondaryDataCropForm initialValue={getSharedSecondaryDataAnswer(secondaryDataCrops)} />
                </div>
              </div>
            )}
            <BulkDataPointSection
              label={doesFormProvideOwnPanel(practiceType) ? undefined : getDataPointLabel(practiceType)}
              mode={mode}
              explainer={getExplainer(practiceType)}
              verifiedFields={getVerifiedFields(practiceType)}
              fields={selectedFields}
            >
              {renderDataPointInputs(practiceType)}
            </BulkDataPointSection>
          </BulkDataPointForm>
        ))}
        <StickyFooterButtons absolute>
          {/* the shared row-space-between class is a flex row with no gap, which butts the two buttons together */}
          <Flex align="center" justify="space-between" gap="1rem" className="width-100">
            <Button dataTestId="bulk-data-drawer-cancel" type="secondary" onClick={onClose}>
              {t("historicalPracticeDataTableView:bulkDataDrawer.cancel")}
            </Button>
            <Button
              dataTestId="bulk-data-drawer-save"
              type="primary"
              onClick={handleSubmit}
              loading={isSaving || isSavingCropData}
              disabled={activeEditedPracticeTypes.length === 0 || writableItems.length === 0}
            >
              {saveButtonLabel}
            </Button>
          </Flex>
        </StickyFooterButtons>
      </Flex>
      <BulkReplaceConfirmationModal
        isVisible={isReplaceConfirmationVisible}
        overwrittenFieldCount={overwrittenFieldIds.length}
        onConfirm={handleSubmit}
        onCancel={() => setIsReplaceConfirmationVisible(false)}
      />
      <UndersowingOverlapWarningModal
        dataTestId="bulk-data-drawer-undersowing-overlap-warning"
        cropNames={undersowingCropsBrokenByChange}
        onCancel={() => {
          setIsUndersowingWarningVisible(false);
          setUndersowingCropsBrokenByChange([]);
        }}
        onConfirm={handleSubmit}
      />
    </Drawer>
  );
};

// One form per opened data point, so their field names never collide and each keeps its inputs while hidden
const BulkDataPointForm = ({ practiceType, isShown, formsByPracticeType, onEdited, children }: BulkDataPointFormProps) => {
  const [form] = Form.useForm();

  useEffect(() => {
    const forms = formsByPracticeType.current;
    forms[practiceType] = form;
    return () => {
      delete forms[practiceType];
    };
  }, [formsByPracticeType, practiceType, form]);

  return (
    <Form
      dataTestId="bulk-data-point-form"
      form={form}
      name={`bulkEditDataForm-${practiceType}`}
      hidden={!isShown}
      preserve={true}
      // only fires on the farmer's own input, never on values the inputs seed themselves
      onValuesChange={() => onEdited(practiceType)}
    >
      {children}
    </Form>
  );
};

// Covers the drawer once the save lands, so the farmer sees it worked before the drawer closes itself
const BulkSaveSuccessOverlay = () => {
  const { t } = useTranslation("historicalPracticeDataTableView");

  return (
    <Flex vertical align="center" justify="center" gap="1.5rem" className="bulk-save-success-overlay" data-testid="bulk-save-success-overlay">
      <Flex align="center" justify="center" className="bulk-save-success-overlay-icon">
        <Icon iconName="tabler/check" size="l" />
      </Flex>
      <Typography variant="h4" color="green-400">
        {t("bulkDataDrawer.successMessage")}
      </Typography>
    </Flex>
  );
};

// "applied to 3/6" on a practice entry, naming the fields on hover. Tells the farmer which of the selected crops an
// existing entry belongs to before they edit it for the whole selection.
const BulkEntryAppliedTag = ({ appliedTo, fields }: BulkEntryAppliedTagProps) => {
  const { t } = useTranslation(["historicalPracticeDataTableView", "farm"]);
  const appliedFields = appliedTo.fieldIds.map((fieldId) => fields.find((field) => field.id === fieldId)).filter((field) => field !== undefined);

  return (
    <Tooltip
      variant="neutral"
      placement="top"
      rootClassName="bulk-entry-applied-tooltip"
      title={
        <Flex vertical gap="0.25rem" className="bulk-entry-applied-fields">
          {appliedFields.map((field) => (
            <Flex key={field.id} align="center" gap="0.5rem">
              <FieldMap field={field} />
              <Typography variant="paragraph-s" className="bulk-entry-applied-field-name">
                {field.name || t("farm:unknownFieldName")}
              </Typography>
            </Flex>
          ))}
        </Flex>
      }
    >
      <TagNormal
        className="bulk-entry-applied-tag"
        color="grey"
        dataTestId="bulk-entry-applied-tag"
        text={t("historicalPracticeDataTableView:bulkDataDrawer.appliedTo", { count: appliedTo.appliedCount, total: appliedTo.selectedCount })}
      />
    </Tooltip>
  );
};

// Last stop before an edit-mode save overwrites values a farmer already entered. Only shown when something would
// actually be replaced, and dismissable for good — the drawer reads that preference before opening it.
const BulkReplaceConfirmationModal = ({ isVisible, overwrittenFieldCount, onConfirm, onCancel }: BulkReplaceConfirmationModalProps) => {
  const { t } = useTranslation("historicalPracticeDataTableView");
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  const handleDismissChange = (isChecked: boolean) => {
    setIsDismissed(isChecked);
    setLocalStorageBulkReplaceConfirmationDismissed(isChecked);
  };

  return (
    <Modal
      className="bulk-replace-confirmation-modal"
      dataTestId="bulk-replace-confirmation-modal"
      showModal={isVisible}
      title={t("bulkDataDrawer.replaceModal.title")}
      okText={t("bulkDataDrawer.replaceModal.confirm")}
      cancelText={t("bulkDataDrawer.replaceModal.cancel")}
      onOk={onConfirm}
      onCancel={onCancel}
      content={
        <Flex vertical gap="1rem">
          <Typography variant="paragraph-m">{t("bulkDataDrawer.replaceModal.description", { count: overwrittenFieldCount })}</Typography>
          <AlertBanner
            type="warning"
            iconName="tabler/alert-circle"
            iconSize={24}
            iconAlign="start"
            description={t("bulkDataDrawer.replaceModal.warning")}
            descriptionSize="paragraph-m"
          />
          <Checkbox data-testid="bulk-replace-confirmation-dismiss" checked={isDismissed} onChange={(event) => handleDismissChange(event.target.checked)}>
            <Typography variant="paragraph-m">{t("bulkDataDrawer.replaceModal.doNotAskAgain")}</Typography>
          </Checkbox>
        </Flex>
      }
    />
  );
};

// Explains what the save will do to the selection before the farmer picks a data point — overwrite existing values
// in edit mode, fill only the empty ones in add mode. Farmers who bulk edit see it on every open, so it collapses to
// its heading and can be dismissed for good; the dismissal is read once when the drawer mounts, so ticking the box
// never yanks the banner away mid-edit
const BulkEditWarningBanner = ({ mode }: BulkEditWarningBannerProps) => {
  const { t } = useTranslation("historicalPracticeDataTableView");
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  const handleDismissChange = (isChecked: boolean) => {
    setIsDismissed(isChecked);
    setLocalStorageBulkEditWarningDismissed(isChecked);
  };

  return (
    <AlertBanner
      className="bulk-edit-warning-banner"
      dataTestId="bulk-edit-warning-banner"
      type="warning"
      iconName="tabler/alert-circle"
      iconSize={24}
      iconAlign="start"
      description={
        <Flex vertical gap="1rem">
          <Flex align="center" justify="space-between" gap="1rem">
            <Typography variant="paragraph-m" bold>
              {t("bulkDataDrawer.warningBanner.title")}
            </Typography>
            <Link dataTestId="bulk-edit-warning-banner-toggle" type="primary-link" onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}>
              {t(isExpanded ? "bulkDataDrawer.warningBanner.showLess" : "bulkDataDrawer.warningBanner.showMore")}
            </Link>
          </Flex>
          {isExpanded && (
            <>
              <Typography variant="paragraph-m">
                {t(mode === BULK_DATA_DRAWER_MODE.add ? "bulkDataDrawer.warningBanner.addDescription" : "bulkDataDrawer.warningBanner.description")}
              </Typography>
              <Checkbox data-testid="bulk-edit-warning-banner-dismiss" checked={isDismissed} onChange={(event) => handleDismissChange(event.target.checked)}>
                <Typography variant="paragraph-m">{t("bulkDataDrawer.warningBanner.doNotShowAgain")}</Typography>
              </Checkbox>
            </>
          )}
        </Flex>
      }
    />
  );
};
